(() => {
  if (window.__sectionMobileCarouselInitialized) return;
  window.__sectionMobileCarouselInitialized = true;

  const MOBILE_QUERY = '(max-width: 749px)';
  const DRAG_THRESHOLD = 8;
  const VELOCITY_THRESHOLD = 0.35;
  const SNAP_DISTANCE_THRESHOLD = 24;

  /**
   * @param {Event} event
   */
  function preventDefault(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Find the slide nearest to the center of the scroller viewport.
   * @param {HTMLElement[]} slides
   * @param {HTMLElement} scroller
   */
  function getActiveSlideIndex(slides, scroller) {
    const viewportCenter = scroller.scrollLeft + scroller.clientWidth / 2;
    let activeIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    slides.forEach((slide, index) => {
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const distance = Math.abs(slideCenter - viewportCenter);

      if (distance < nearestDistance) {
        nearestDistance = distance;
        activeIndex = index;
      }
    });

    return activeIndex;
  }

  /**
   * @param {MediaQueryList} mediaQuery
   * @param {() => void} callback
   */
  function bindMediaQueryChange(mediaQuery, callback) {
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', callback);
      return;
    }

    if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(callback);
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function initCarousel(root) {
    if (root.dataset.mobileCarouselInitialized === 'true') return;

    const scroller = root.querySelector('[data-mobile-carousel-scroller]');
    if (!(scroller instanceof HTMLElement)) return;

    const slides = Array.from(scroller.children).filter((child) => child instanceof HTMLElement);
    if (slides.length < 2) return;

    root.dataset.mobileCarouselInitialized = 'true';

    const controls = root.querySelector('[data-mobile-carousel-controls]');
    const dots = controls ? Array.from(controls.querySelectorAll('[data-mobile-carousel-dot]')) : [];
    const current = controls ? controls.querySelector('[data-mobile-carousel-current]') : null;
    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frameId = 0;
    let activeIndex = -1;
    let dragAbortController = null;

    const setActive = (nextIndex) => {
      const safeIndex = Math.max(0, Math.min(nextIndex, slides.length - 1));
      if (safeIndex === activeIndex) return;

      activeIndex = safeIndex;

      dots.forEach((dot, index) => {
        dot.setAttribute('aria-selected', `${index === activeIndex}`);
      });

      if (current) current.textContent = `${activeIndex + 1}`;
    };

    const syncToScrollPosition = () => {
      if (!mediaQuery.matches) return;
      setActive(getActiveSlideIndex(slides, scroller));
    };

    const scheduleSync = () => {
      if (frameId) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(syncToScrollPosition);
    };

    const scrollToSlide = (index) => {
      const target = slides[index];
      if (!(target instanceof HTMLElement)) return;

      scroller.scrollTo({
        left: Math.max(0, target.offsetLeft),
        behavior: prefersReducedMotion.matches ? 'auto' : 'smooth',
      });
    };

    const scrollToNearestSlide = (dragVelocity = 0, dragDistance = 0) => {
      const currentIndex = getActiveSlideIndex(slides, scroller);
      let nextIndex = currentIndex;

      if (Math.abs(dragVelocity) >= VELOCITY_THRESHOLD || Math.abs(dragDistance) >= SNAP_DISTANCE_THRESHOLD) {
        if (dragVelocity > 0 || dragDistance > SNAP_DISTANCE_THRESHOLD) {
          nextIndex = Math.max(0, currentIndex - 1);
        } else if (dragVelocity < 0 || dragDistance < -SNAP_DISTANCE_THRESHOLD) {
          nextIndex = Math.min(slides.length - 1, currentIndex + 1);
        }
      }

      scrollToSlide(nextIndex);
      setActive(nextIndex);
    };

    const endDragState = () => {
      root.removeAttribute('data-mobile-carousel-dragging');
      scroller.style.removeProperty('scroll-snap-type');
      scroller.style.removeProperty('scroll-behavior');
    };

    /**
     * @param {PointerEvent} event
     */
    const handlePointerDown = (event) => {
      if (!mediaQuery.matches) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (dragAbortController) dragAbortController.abort();

      const controller = new AbortController();
      const { signal } = controller;
      dragAbortController = controller;

      const startX = event.clientX;
      const startY = event.clientY;
      const startScrollLeft = scroller.scrollLeft;
      let previousX = event.clientX;
      let previousTime = performance.now();
      let dragVelocity = 0;
      let dragDistance = 0;
      let axisLocked = null;
      let dragging = false;

      const cleanup = () => {
        controller.abort();
        dragAbortController = null;
        endDragState();
      };

      /**
       * @param {PointerEvent} moveEvent
       */
      const handlePointerMove = (moveEvent) => {
        const deltaX = moveEvent.clientX - startX;
        const deltaY = moveEvent.clientY - startY;

        if (!axisLocked) {
          if (Math.abs(deltaX) < DRAG_THRESHOLD && Math.abs(deltaY) < DRAG_THRESHOLD) return;

          if (Math.abs(deltaY) > Math.abs(deltaX)) {
            cleanup();
            return;
          }

          axisLocked = 'x';
          dragging = true;
          root.setAttribute('data-mobile-carousel-dragging', '');
          scroller.style.setProperty('scroll-snap-type', 'none');
          scroller.style.setProperty('scroll-behavior', 'auto');

          if (typeof scroller.setPointerCapture === 'function') {
            try {
              scroller.setPointerCapture(moveEvent.pointerId);
            } catch {}
          }

          document.addEventListener('click', preventDefault, { capture: true, once: true, signal });
        }

        if (axisLocked !== 'x') return;

        moveEvent.preventDefault();
        moveEvent.stopPropagation();

        const now = performance.now();
        const timeDelta = Math.max(now - previousTime, 1);
        const movedX = moveEvent.clientX - previousX;

        dragVelocity = movedX / timeDelta;
        dragDistance = deltaX;
        previousX = moveEvent.clientX;
        previousTime = now;

        scroller.scrollLeft = startScrollLeft - deltaX;
        scheduleSync();
      };

      /**
       * @param {PointerEvent} endEvent
       */
      const handlePointerEnd = (endEvent) => {
        if (!dragging) {
          cleanup();
          return;
        }

        if (typeof scroller.releasePointerCapture === 'function') {
          try {
            scroller.releasePointerCapture(endEvent.pointerId);
          } catch {}
        }

        endDragState();
        controller.abort();
        dragAbortController = null;
        scrollToNearestSlide(dragVelocity, dragDistance);
      };

      document.addEventListener('pointermove', handlePointerMove, { signal });
      document.addEventListener('pointerup', handlePointerEnd, { signal });
      document.addEventListener('pointercancel', handlePointerEnd, { signal });
    };

    const goToSlide = (index) => {
      if (!Number.isFinite(index)) return;
      scrollToSlide(index);
      setActive(index);
    };

    dots.forEach((dot) => {
      dot.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        goToSlide(Number(dot.dataset.slideIndex));
      });
    });

    if (controls instanceof HTMLElement) {
      controls.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const dot = target.closest('[data-mobile-carousel-dot]');
        if (!(dot instanceof HTMLElement)) return;

        event.preventDefault();
        event.stopPropagation();
        goToSlide(Number(dot.dataset.slideIndex));
      });
    }

    scroller.addEventListener('pointerdown', handlePointerDown);
    scroller.addEventListener('scroll', scheduleSync, { passive: true });
    window.addEventListener('resize', scheduleSync, { passive: true });
    bindMediaQueryChange(mediaQuery, scheduleSync);
    requestAnimationFrame(syncToScrollPosition);
  }

  /**
   * @param {ParentNode} [scope]
   */
  function initAllCarousels(scope = document) {
    const roots = scope.querySelectorAll('[data-mobile-carousel-root]');
    roots.forEach((root) => {
      if (root instanceof HTMLElement) initCarousel(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllCarousels());
  } else {
    initAllCarousels();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    initAllCarousels(event.target);
  });
})();
